import { useMemo, useState, type FormEvent } from 'react';
import { useNavigation, useSubmit } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '@understory-io/leaf/button';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { TextArea, TextField } from '@understory-io/leaf/text-field';
import { css } from '@understory-io/leaf/css';
import { SegmentedControl } from '@understory-io/leaf/segmented-control';

import { storefrontContext } from '~/domain/core/index.server';
import { loggerContext } from '~/lib/logger';
import {
  getBasketId,
  getGiftCardBasket,
  type GiftCardBasketView,
} from '~/domain/basket/index.server';
import { inFlight } from '~/lib/parallel';
import { getGiftCardConfig } from '~/domain/giftcard/index.server';
import {
  getMaximumGiftCardAmount,
  MINIMUM_GIFT_CARD_AMOUNT,
} from '~/domain/giftcard/pricing';
import type { GiftCardSelectionType } from '~/domain/giftcard/selection';
import {
  deriveExperiencePriceCents,
  type GiftCardGuests,
} from '~/domain/giftcard/experience';
import { notFound } from '~/lib/react-router';
import { CheckoutMain } from '~/routes/checkout/checkout-shell';
import { FormattedCurrency } from '~/ui/formatted';

import type { Route } from './+types/route';
import { handleGiftCardSubmit } from './actions.server';
import { loadGiftCardExperiences } from './loader.experiences.server';
import { ExperienceFields } from './experience-fields';
import { GIFT_CARD_GREETING_MAX_LENGTH } from './schema';

export async function loader({ request, context }: Route.LoaderArgs) {
  const storefront = context.get(storefrontContext);
  const logger = context.get(loggerContext);

  if (!storefront.preferences.giftCards) throw notFound();

  // The picker read bets that the storefront sells experience gift cards, so
  // it doesn't wait on the config to find out; when the bet loses, the read is
  // dropped.
  const experiencesRead = inFlight(loadGiftCardExperiences(storefront, logger));
  const basketRead = inFlight(readGiftCardBasket(request));
  const config = await getGiftCardConfig(storefront.companyId, logger);

  const experiences = config.allowExperienceGiftCards
    ? await experiencesRead
    : [];
  const allowStandard = config.allowAmountGiftCards;
  const allowExperience =
    config.allowExperienceGiftCards && experiences.length > 0;
  if (!allowStandard && !allowExperience) throw notFound();

  // Restore a selection already in the basket (e.g. Back from checkout), so the
  // form isn't reset. Sanitised against the current offering below.
  const initialSelection = resolveInitialSelection(await basketRead, {
    allowStandard,
    allowExperience,
    experiences,
  });

  return {
    currency: storefront.defaultCurrency,
    minAmount: MINIMUM_GIFT_CARD_AMOUNT,
    maxAmount: getMaximumGiftCardAmount(storefront.defaultCurrency),
    allowStandard,
    allowExperience,
    experiences,
    expiryPeriod: config.expiryPeriod,
    initialSelection,
  };
}

async function readGiftCardBasket(
  request: Request
): Promise<GiftCardBasketView | null> {
  const basketId = await getBasketId(request);
  return basketId ? getGiftCardBasket(basketId) : null;
}

type InitialSelection = {
  type: GiftCardSelectionType;
  amount: string;
  experienceId: string | null;
  guests: GiftCardGuests;
  greeting: string;
};

/**
 * Seed the form from the stored gift-card basket, validating against what's
 * currently offered: an experience selection is kept only if the experience is
 * still available and its guest quantities map to existing variants; a stored
 * type that's no longer offered falls back to the default. The greeting is
 * always preserved.
 */
function resolveInitialSelection(
  basket: GiftCardBasketView | null,
  {
    allowStandard,
    allowExperience,
    experiences,
  }: {
    allowStandard: boolean;
    allowExperience: boolean;
    experiences: Awaited<ReturnType<typeof loadGiftCardExperiences>>;
  }
): InitialSelection {
  // Pre-select the first experience so the Experience tab shows its tickets
  // immediately (no empty picker). Guests stay empty until the visitor picks.
  const empty: InitialSelection = {
    type: allowStandard ? 'standard' : 'experience',
    amount: '',
    experienceId: allowExperience ? (experiences[0]?.id ?? null) : null,
    guests: {},
    greeting: '',
  };

  if (!basket) return empty;

  const greeting = basket.greeting ?? '';

  if (basket.type === 'experience' && allowExperience) {
    const experience = experiences.find((e) => e.id === basket.experienceId);
    if (experience) {
      // Both variant and add-on ids are valid selections to restore.
      const validIds = new Set(
        experience.variants.flatMap((v) => [
          v.id,
          ...(v.addOns ?? []).map((a) => a.id),
        ])
      );
      const guests: GiftCardGuests = {};
      for (const [id, quantity] of Object.entries(basket.people ?? {})) {
        if (validIds.has(id) && quantity > 0) guests[id] = quantity;
      }
      return {
        type: 'experience',
        amount: '',
        experienceId: experience.id,
        guests,
        greeting,
      };
    }
  }

  if (basket.type === 'standard' && allowStandard) {
    return {
      type: 'standard',
      amount: basket.priceCents != null ? String(basket.priceCents / 100) : '',
      experienceId: null,
      guests: {},
      greeting,
    };
  }

  // Stored selection no longer maps to the current offering — keep the greeting.
  return { ...empty, greeting };
}

export async function action({ request, context, params }: Route.ActionArgs) {
  const storefront = context.get(storefrontContext);
  const logger = context.get(loggerContext);
  const experiencesRead = inFlight(loadGiftCardExperiences(storefront, logger));
  const config = await getGiftCardConfig(storefront.companyId, logger);
  const experiences = config.allowExperienceGiftCards
    ? await experiencesRead
    : [];
  return handleGiftCardSubmit({
    request,
    currency: storefront.defaultCurrency,
    locale: params.locale,
    experiences,
  });
}

export default function GiftCardAmountPage({
  loaderData,
  actionData,
}: Route.ComponentProps) {
  const intl = useIntl();
  const submit = useSubmit();
  const navigation = useNavigation();
  const {
    minAmount,
    maxAmount,
    allowStandard,
    allowExperience,
    experiences,
    expiryPeriod,
    initialSelection,
  } = loaderData;
  const currencyLabel = loaderData.currency.toUpperCase();

  // Validity disclaimer (e.g. "valid for 1 year from the date of purchase"),
  // mirroring the legacy flow. One ICU message selects the unit and pluralizes.
  const validityDisclaimer = intl.formatMessage(
    {
      defaultMessage:
        'The gift card is valid for {value, plural, one {# {unit, select, years {year} other {month}}} other {# {unit, select, years {years} other {months}}}} from the date of purchase.',
      description: 'Gift card validity disclaimer shown on the gift card page',
    },
    { value: expiryPeriod.value, unit: expiryPeriod.unit }
  );

  const [type, setType] = useState<GiftCardSelectionType>(
    initialSelection.type
  );
  const [amount, setAmount] = useState(initialSelection.amount);
  const [experienceId, setExperienceId] = useState<string | null>(
    initialSelection.experienceId
  );
  const [guests, setGuests] = useState<GiftCardGuests>(initialSelection.guests);
  // Controlled so the live character counter below the field can react as the
  // visitor types (mirroring the legacy flow's "X / 140" helper).
  const [greeting, setGreeting] = useState(initialSelection.greeting);

  // Live subtotal shown inside the Continue button ("Continue · 598 DKK"). For
  // an experience it's the sum of the selected ticket prices; for a fixed
  // amount it's the entered value. Zero (nothing selected / empty amount) shows
  // a plain "Continue".
  const subtotalCents = useMemo(() => {
    if (type === 'experience') {
      const experience = experiences.find((e) => e.id === experienceId);
      return experience ? deriveExperiencePriceCents(experience, guests) : 0;
    }
    const parsed = Number.parseFloat(amount.replace(',', '.'));
    return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) : 0;
  }, [type, amount, experienceId, guests, experiences]);

  const isSubmitting = navigation.state === 'submitting';
  const onContinue = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit(new FormData(event.currentTarget), { method: 'post' });
  };

  const range = {
    min: intl.formatNumber(minAmount),
    max: intl.formatNumber(maxAmount),
    currency: currencyLabel,
  };
  const amountCode = actionData?.errors?.amount;
  const greetingCode = actionData?.errors?.greeting;
  const amountErrorProps = amountCode
    ? {
        tone: 'critical' as const,
        message:
          amountCode === 'required'
            ? intl.formatMessage({
                defaultMessage: 'Please enter an amount',
                description: 'Error when the gift card amount is empty',
              })
            : intl.formatMessage(
                {
                  defaultMessage:
                    'Enter an amount between {min} and {max} {currency}',
                  description:
                    'Error when the gift card amount is out of range',
                },
                range
              ),
      }
    : {};
  const greetingErrorProps = greetingCode
    ? {
        tone: 'critical' as const,
        message: intl.formatMessage(
          {
            defaultMessage: 'The message can be at most {max} characters',
            description: 'Error when the gift card greeting is too long',
          },
          { max: GIFT_CARD_GREETING_MAX_LENGTH }
        ),
      }
    : {};
  const experienceError = actionData?.errors?.experienceId
    ? intl.formatMessage({
        defaultMessage: 'Please select an experience',
        description: 'Error when no experience is chosen for a gift card',
      })
    : undefined;
  const guestsError = actionData?.errors?.guests
    ? intl.formatMessage({
        defaultMessage: 'Select at least one ticket',
        description:
          'Error when no tickets are chosen for an experience gift card',
      })
    : undefined;

  const subtitle =
    allowStandard && allowExperience
      ? intl.formatMessage({
          defaultMessage:
            'Choose between a gift card with a set amount or for a specific experience, and add a personal greeting.',
          description: 'Gift card page subtitle when both types are offered',
        })
      : allowExperience
        ? intl.formatMessage({
            defaultMessage:
              'Choose an experience for your gift card and add a personal greeting.',
            description: 'Gift card page subtitle for experience gift cards',
          })
        : intl.formatMessage({
            defaultMessage:
              'Choose an amount for your gift card and add a personal greeting.',
            description: 'Gift card page subtitle for amount gift cards',
          });

  return (
    <CheckoutMain>
      <form method="post" noValidate onSubmit={onContinue}>
        <input type="hidden" name="type" value={type} />
        {type === 'experience' && (
          <>
            <input
              type="hidden"
              name="experienceId"
              value={experienceId ?? ''}
            />
            {Object.entries(guests).map(([variantId, quantity]) => (
              <input
                key={variantId}
                type="hidden"
                name={`guest.${variantId}`}
                value={quantity}
              />
            ))}
          </>
        )}

        <Stack gap="large">
          <Stack gap="tiny">
            <Text as="h1" style="headline">
              <FormattedMessage
                defaultMessage="Buy a gift card"
                description="Heading of the gift card amount-selection page"
              />
            </Text>
            <Text style="body-small-regular" color="secondary">
              {subtitle}
            </Text>
            <Text style="body-small-regular" color="tertiary">
              {validityDisclaimer}
            </Text>
          </Stack>

          {allowStandard && allowExperience && (
            <SegmentedControl<GiftCardSelectionType>
              label={intl.formatMessage({
                defaultMessage: 'Gift card type',
                description: 'Label for the gift card type toggle',
              })}
              options={[
                {
                  value: 'standard',
                  label: intl.formatMessage({
                    defaultMessage: 'Amount',
                    description: 'Standard (fixed-amount) gift card option',
                  }),
                },
                {
                  value: 'experience',
                  label: intl.formatMessage({
                    defaultMessage: 'Experience',
                    description: 'Experience gift card option',
                  }),
                },
              ]}
              value={type}
              onChange={setType}
            />
          )}

          <Stack gap="medium">
            {type === 'standard' ? (
              <TextField
                label={intl.formatMessage({
                  defaultMessage: 'Amount for gift card',
                  description: 'Label for the gift card amount field',
                })}
                placeholder={intl.formatMessage({
                  defaultMessage: 'Enter amount',
                  description: 'Placeholder for the gift card amount field',
                })}
                name="amount"
                autoComplete="off"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                {...amountErrorProps}
              />
            ) : (
              <ExperienceFields
                experiences={experiences}
                experienceId={experienceId}
                guests={guests}
                onSelectExperience={(id) => {
                  setExperienceId(id);
                  setGuests({});
                }}
                onGuestsChange={setGuests}
                experienceError={experienceError}
                guestsError={guestsError}
              />
            )}

            <Stack gap="tiny">
              <TextArea
                label={intl.formatMessage({
                  defaultMessage: 'Personal greeting (optional)',
                  description:
                    'Label for the optional gift card greeting message',
                })}
                name="greeting"
                rows={3}
                value={greeting}
                onChange={(event) => setGreeting(event.target.value)}
                maxLength={GIFT_CARD_GREETING_MAX_LENGTH}
                placeholder={intl.formatMessage({
                  defaultMessage: 'Write a personal greeting (optional)',
                  description: 'Placeholder for the gift card greeting message',
                })}
                className={css({
                  '& textarea': { resize: 'none', height: 'auto' },
                })}
                {...greetingErrorProps}
              />
              <Text
                style="body-small-regular"
                color="tertiary"
                className={css({ alignSelf: 'flex-end' })}
              >
                <FormattedMessage
                  defaultMessage="{count} / {max}"
                  description="Character counter shown under the gift card greeting message"
                  values={{
                    count: greeting.length,
                    max: GIFT_CARD_GREETING_MAX_LENGTH,
                  }}
                />
              </Text>
            </Stack>
          </Stack>

          <Button
            type="submit"
            appearance="primary"
            size="large"
            busy={isSubmitting}
          >
            {subtotalCents > 0 ? (
              <FormattedMessage
                defaultMessage="Continue <price>· {amount}</price>"
                description="Submit button on the gift card amount page, with the live subtotal appended (the price part is regular weight)"
                values={{
                  amount: (
                    <FormattedCurrency
                      value={subtotalCents / 100}
                      currency={loaderData.currency}
                    />
                  ),
                  // The subtotal reads lighter than the "Continue" label, per
                  // the Figma (Inter Regular 400 vs. the button's Medium 600).
                  price: (chunks) => (
                    <span className={css({ fontWeight: 'bodyLargeRegular' })}>
                      {chunks}
                    </span>
                  ),
                }}
              />
            ) : (
              <FormattedMessage
                defaultMessage="Continue"
                description="Submit button on the gift card amount page"
              />
            )}
          </Button>
        </Stack>
      </form>
    </CheckoutMain>
  );
}
